import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import {
  searchParams,
  statusParams,
  type ObjectStatus,
  type ResourceUsage,
} from '@/services/core/listSearch'
import { SitesApi } from '@/services/sites/sitesApi'
import { AdUnitsApi } from '@/services/adUnits/adUnitsApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface AdvertiserFloorPrice {
  team?: { id: string; name: string }
  advertiser?: { id: string; name: string }
  floorPrice: { currency: string; amount: number }
}

export interface Ruleset {
  id: string
  name: string
  objectState: string
  type: string
  floorPrice: { currency: string; amount: number } | null
  advertiserFloorPrices: AdvertiserFloorPrice[]
  targetingTypes: string[]
  sizes: { dimensions: number[] }[]
  layouts: { id: string; name: string }[]
  teams: { id: string; name: string }[]
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

export interface AdvertiserFloorPriceInput {
  team?: { id: string }
  advertiser?: { id: string }
  floorPrice: { currency: string; amount: number }
}

export interface RulesetInput {
  name: string
  type?: string
  objectState?: string
  floorPrice?: { currency: string; amount: number } | null
  advertiserFloorPrices?: AdvertiserFloorPriceInput[]
  targetingTypes?: string[]
  sizes?: { dimensions: number[] }[]
  layouts?: { id: string }[]
  teams?: { id: string }[]
}

function mapAdvertiserFloorPrice(r: Record<string, unknown>): AdvertiserFloorPrice {
  const team = r.team as Record<string, unknown> | undefined
  const adv = r.advertiser as Record<string, unknown> | undefined
  const fp = r.floorPrice as Record<string, unknown>
  return {
    ...(team ? { team: { id: (team.id as string) ?? '', name: (team.name as string) ?? '' } } : {}),
    ...(adv ? { advertiser: { id: (adv.id as string) ?? '', name: (adv.name as string) ?? '' } } : {}),
    floorPrice: { currency: (fp?.currency as string) ?? 'EUR', amount: (fp?.amount as number) ?? 0 },
  }
}

function mapRuleset(r: Record<string, unknown>): Ruleset {
  const teams = (r.teams as Record<string, unknown>[]) ?? []
  const fp = r.floorPrice as Record<string, unknown> | undefined
  const afp = (r.advertiserFloorPrices as Record<string, unknown>[]) ?? []
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    type: (r.type as string) ?? '',
    floorPrice: fp ? { currency: (fp.currency as string) ?? 'EUR', amount: (fp.amount as number) ?? 0 } : null,
    advertiserFloorPrices: afp.map(mapAdvertiserFloorPrice),
    targetingTypes: (r.targetingTypes as string[]) ?? [],
    sizes: ((r.sizes as Record<string, unknown>[]) ?? []).map((s) => ({
      dimensions: (s.dimensions as number[]) ?? [],
    })),
    layouts: ((r.layouts as Record<string, unknown>[]) ?? []).map((l) => ({
      id: (l.id as string) ?? '',
      name: (l.name as string) ?? '',
    })),
    teams: teams.map((t) => ({ id: (t.id as string) ?? '', name: (t.name as string) ?? '' })),
    createTime: (r.createTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** The options a ruleset list page is narrowed by on the server. */
export interface RulesetListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of rulesets, narrowed by search and state on the server. */
async function fetchRulesetPage(
  search: string | undefined,
  status: ObjectStatus | undefined,
  page: number,
  pageSize: number,
  sort?: ListSort | null
): Promise<ListPage<Ruleset>> {
  const params = new URLSearchParams({
    ...searchParams('rulesets', search),
    concise: 'true',
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/rulesets?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch rulesets')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapRuleset)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

/** The field each ruleset type is assigned through on a publisher, site or ad unit. */
const ASSIGN_FIELDS: Record<string, string> = {
  INVENTORY: 'siteRuleset',
  TARGETING: 'targetingRulesets',
  SIZE: 'sizeRulesets',
  LAYOUT: 'layoutRulesets',
}

/** Adds a ruleset to, and removes it from, publishers, sites or ad units. */
async function assignRuleset(
  endpoint: 'earningsaccounts' | 'sites' | 'adunits',
  rulesetId: string,
  type: string,
  add: string[],
  remove: string[]
): Promise<void> {
  const ids = [...add, ...remove]
  const field = ASSIGN_FIELDS[type]
  if (!ids.length || !field) return
  let body: Record<string, unknown>[]
  if (field === 'siteRuleset') {
    // An object holds one inventory ruleset, so adding replaces the one it has.
    body = [
      ...add.map((id) => ({ id, siteRuleset: { id: rulesetId } })),
      ...remove.map((id) => ({ id, siteRuleset: null })),
    ]
  } else {
    // A list field is saved whole, so each object's current list is read first.
    body = await Promise.all(
      ids.map(async (id) => {
        const res = await apiFetch(`${API_BASE}/${endpoint}/${id}?context=${getContext()}`, {
          headers: JSON_HEADERS,
        })
        if (!res.ok) await throwApiError(res, 'Failed to fetch ruleset usage')
        const data = (await res.json()) as Record<string, unknown>
        const current = ((data[field] as { id: string }[]) ?? []).map((r) => r.id)
        const next = add.includes(id)
          ? [...new Set([...current, rulesetId])]
          : current.filter((r) => r !== rulesetId)
        return { id, [field]: next.map((r) => ({ id: r })) }
      })
    )
  }
  const res = await apiFetch(`${API_BASE}/${endpoint}?context=${getContext()}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  })
  if (!res.ok) await throwApiError(res, 'Failed to assign ruleset')
}

const diff = (before: { id: string }[], after: { id: string }[]) => ({
  added: after.filter((a) => !before.some((b) => b.id === a.id)).map((a) => a.id),
  removed: before.filter((b) => !after.some((a) => a.id === b.id)).map((b) => b.id),
})

export const RulesetsApi = {
  /** The publishers, sites and ad units the ruleset applies to. */
  getUsage: async (id: string): Promise<ResourceUsage> => {
    const ref = ({ id, name }: { id: string; name: string }) => ({ id, name })
    const [publishers, sites, adUnits] = await Promise.all([
      EarningsAccountsApi.getByUsage('siteRuleset', id),
      SitesApi.getByUsage('siteRuleset', id),
      AdUnitsApi.getByUsage('siteRuleset', id),
    ])
    return { publishers: publishers.map(ref), sites: sites.map(ref), adUnits: adUnits.map(ref) }
  },

  /** Saves the ruleset onto whatever was added to its usage, and off whatever was removed. */
  setUsage: async (
    id: string,
    type: string,
    before: ResourceUsage,
    after: ResourceUsage
  ): Promise<void> => {
    const publishers = diff(before.publishers, after.publishers)
    const sites = diff(before.sites, after.sites)
    const adUnits = diff(before.adUnits, after.adUnits)
    await assignRuleset('earningsaccounts', id, type, publishers.added, publishers.removed)
    await assignRuleset('sites', id, type, sites.added, sites.removed)
    await assignRuleset('adunits', id, type, adUnits.added, adUnits.removed)
  },

  /** One page of rulesets, filtered by search and state on the server. */
  getPage: ({ page = 1, pageSize = 25, search, status, sort }: RulesetListOptions = {}) =>
    fetchRulesetPage(search, status, page, pageSize, sort),

  /** Every ruleset matching the filters, walked page by page — for downloads. */
  getAllPages: (search?: string, status?: ObjectStatus): Promise<Ruleset[]> =>
    fetchAllPages((page, pageSize) => fetchRulesetPage(search, status, page, pageSize)),

  getAll: async (search?: string): Promise<Ruleset[]> => {
    const params = new URLSearchParams({
      ...searchParams('rulesets', search),
      concise: 'true',
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/rulesets?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch rulesets')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapRuleset)
  },

  getById: async (id: string): Promise<Ruleset> => {
    const res = await apiFetch(`${API_BASE}/rulesets/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ruleset')
    return mapRuleset(await res.json())
  },

  create: async (id: string, input: RulesetInput): Promise<Ruleset> => {
    const body = { id, ...input }
    const res = await apiFetch(`${API_BASE}/rulesets/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create ruleset')
    return mapRuleset(await res.json())
  },

  update: async (id: string, input: RulesetInput): Promise<Ruleset> => {
    const res = await apiFetch(`${API_BASE}/rulesets/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save ruleset')
    return mapRuleset(await res.json())
  },
}
